import { EventEmitter } from "../utils/event-emitter";
import { GridClientContext, AccountState } from "../types/common";

/**
 * @hidden
 * Event map for account state changes
 */
interface AccountEvents {
  change: AccountState;
}

/**
 * @hidden
 * Manages account state with reactive updates via events
 *
 * Important: Does not cache state. Always derives from shared context for safe singleton usage.
 *
 * @example
 * ```typescript
 * const state = client.account.get();
 * console.log(state.accountAddress, state.isConnected);
 *
 * client.account.on('change', (state) => {
 *   console.log('Account changed:', state.accountAddress);
 * });
 *
 * client.account.set('account-address', 'grid-user-id');
 * client.account.disconnect();
 * ```
 */
export class AccountManager {
  private context: GridClientContext;
  private eventEmitter: EventEmitter<AccountEvents>;

  constructor(context: GridClientContext) {
    this.context = context;
    this.eventEmitter = new EventEmitter<AccountEvents>();
  }

  /**
   * Get current account state (always fresh from context)
   *
   * @returns Current account state
   */
  get(): AccountState {
    const address = this.context.user?.address;
    const gridUserId = this.context.user?.gridUserId;
    const isConnected = !!(address && gridUserId);

    return {
      accountAddress: address,
      gridUserId: gridUserId,
      isConnected,
    };
  }

  /**
   * Set account state and emit change event
   *
   * @param address - Smart account address
   * @param gridUserId - Grid user identifier
   */
  set(address: string, gridUserId: string): void {
    if (!this.context.user) {
      this.context.user = { signers: [] };
    }

    this.context.user.address = address;
    this.context.user.gridUserId = gridUserId;

    this.eventEmitter.emit('change', this.get());
  }

  /**
   * Disconnect account and emit change event
   *
   * Note: Preserves other user context fields (signers, session, email) for re-authentication
   */
  disconnect(): void {
    if (this.context.user) {
      this.context.user.address = undefined;
      this.context.user.gridUserId = undefined;
    }

    this.eventEmitter.emit('change', this.get());
  }

  /**
   * Subscribe to account state changes
   *
   * @param event - Event name ('change')
   * @param handler - Callback receiving new account state
   */
  on(event: 'change', handler: (state: AccountState) => void): void {
    this.eventEmitter.on(event, handler);
  }

  /**
   * Unsubscribe from account state changes
   *
   * @param event - Event name ('change')
   * @param handler - Handler function to remove
   */
  off(event: 'change', handler: (state: AccountState) => void): void {
    this.eventEmitter.off(event, handler);
  }

  /** @hidden Internal method to emit change events */
  _emitChange(): void {
    this.eventEmitter.emit('change', this.get());
  }
}
